import { useQuery } from '@apollo/client'
import { Box, CircularProgress, Modal, styled } from '@mui/material'
import { useTrackVideoEvent } from 'app/_hooks/useTrackVideoEvent'
import { useEffect } from 'react'
import { GetVideoPreviewModalQuery } from '~/__generated__/graphql'
import { useVideoPreviewModalActions } from '~/features/video/hooks/useVideoPreviewModalActions'
import { Colors } from '~/libs/mui/color'
import { VideoPreviewModalContent } from './VideoPreviewModalContent'
import { GET_VIDEO_PREVIEW_MODAL_QUERY } from './graphql/queries/GetVideoPreviewModal'

const ModalFrame = styled(Box)(() => ({
  position: 'relative',
  width: '90%',
  maxWidth: '600px',
  borderRadius: '12px',
  padding: 16,
  backgroundColor: Colors.color1A1A18,
}))

type Props = {
  videoId: string
  playlistId?: string
  attributionToken?: string
  onClose: () => void
}

export const VideoPreviewModal = ({ videoId, playlistId, attributionToken, onClose }: Props) => {
  const { data } = useQuery<GetVideoPreviewModalQuery>(GET_VIDEO_PREVIEW_MODAL_QUERY, {
    variables: { id: videoId },
  })
  const isLoaded = data?.originalVideo != null
  const { trackVideoEvent } = useTrackVideoEvent()

  useEffect(() => {
    if (!isLoaded || !attributionToken) return
    trackVideoEvent({
      variables: { eventType: 'DETAIL_PAGE_VIEW', videoId, attributionToken },
    })
  }, [isLoaded, videoId, attributionToken, trackVideoEvent])
  const { onClose: handleClose, ...videoPreviewModalActions } = useVideoPreviewModalActions({
    videoId,
    onClose,
  })

  return (
    <Modal
      open
      onClose={handleClose}
      aria-labelledby='video-preview-title'
      aria-describedby='video-preview-description'
      /**
       * NOTE: MUIはz-indexの設定が modal(1300) > drawer(1200)になっている。
       * プレイリストアクションUIがmodalとdrawerの2つが同時に表示されるケースがあるため、それらよりも下にプレビューモーダルが表示されるように1199を指定
       */
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1199 }}
    >
      <ModalFrame>
        {!data?.originalVideo ? (
          <Box display='flex' justifyContent='center' padding={4}>
            <CircularProgress color='primary' />
          </Box>
        ) : (
          <VideoPreviewModalContent
            videoPreviewModalInfo={data.originalVideo}
            videoPreviewModalActions={videoPreviewModalActions}
            playlistId={playlistId}
          />
        )}
      </ModalFrame>
    </Modal>
  )
}
